import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ValidateMemberInvitation } from "../services/authAPI";
import { Typography, Box, Button, Container, Alert, AlertTitle } from "@mui/material";
import { Link } from "react-router-dom";

import { AuthContext } from "../contexts/AuthContext";

function ValidateInviteToken() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const { logout, isLoggedIn } = useContext(AuthContext);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError("Invalid or missing token");
        return;
      }
      if (isLoggedIn) {
        logout(true); // Skip redirect since we're handling navigation
      }
      setLoading(true);
      try {
        const response = await ValidateMemberInvitation(token);
        if (response && response.is_existing_member === true) {

          navigate(`/member/register-existing-member?token=${token}`, { replace: true });
        } else {
          navigate(`/member/member-register?token=${token}`, { replace: true });
        }
      } catch (err) {
        console.error("Error validating token:", err);
        setError("invalid_token");
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [token, navigate, logout, isLoggedIn]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ backgroundColor: "#f5f5f5" }}
    >
      <Container maxWidth="sm">
        <Box sx={{ backgroundColor: "white", borderRadius: 2, p: 4, boxShadow: 2 }}>
          {loading && (
            <Box textAlign="center">
              <Typography variant="h5" gutterBottom>
                Validating Your Invitation...
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please wait while we verify your registration link.
              </Typography>
            </Box>
          )}

          {error && (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <AlertTitle>Unable to Validate Registration Link</AlertTitle>
                We couldn't process your registration invitation at this time.
              </Alert>

              <Typography variant="h6" gutterBottom>
                What happened?
              </Typography>
              <Typography variant="body1" paragraph color="text.secondary">
                Your registration link may have expired, or you may have already completed your registration.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                What should you do?
              </Typography>
              <Box component="ul" sx={{ pl: 2, color: "text.secondary" }}>
                <li>
                  <Typography variant="body1" paragraph>
                    <strong>If you've already registered:</strong> Try logging in with your credentials using the button below.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" paragraph>
                    <strong>If you haven't registered yet:</strong> Contact the person who sent you the invitation and request a new registration link.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1" paragraph>
                    <strong>Need help?</strong> Reach out to your account administrator to confirm your invitation is still valid.
                  </Typography>
                </li>
              </Box>

              <Box sx={{ display: "flex", gap: 2, mt: 4, flexDirection: { xs: "column", sm: "row" } }}>
                <Button
                  component={Link}
                  to="/login"
                  variant="contained"
                  color="primary"
                  fullWidth
                >
                  Go to Login
                </Button>
                <Button
                  component={Link}
                  to="/"
                  variant="outlined"
                  color="primary"
                  fullWidth
                >
                  Return Home
                </Button>
              </Box>
            </>
          )}

          {success && (
            <Box textAlign="center">
              <Typography variant="h5" color="primary" gutterBottom>
                {success}
              </Typography>
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default ValidateInviteToken;